import React, { useState, useEffect } from "react";
import { Modal, FormInput, Button, SearchableSelect, ConfirmationModal, TableActionButton } from "@/components/ui";
import { UploadCloud, X, File, FileVideo, Image as ImageIcon, Eye, Trash2, Edit } from "lucide-react";
import { useDateFilteredLeads } from "@/hooks/useDateFilteredData";
import { designService } from "../services/designService";
import { BASE_URL } from "@/lib/API";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { useNavigate } from "react-router-dom";

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from "@/store/authStore";
import { useTeam } from "../hooks/useTeam";

export const AddDesign = ({ isOpen, onClose, onSubmit, initialData, targetStatus = null, submitLabel, titleOverride = null, acceptFiles, children }) => {
  const modalTitle = titleOverride ? titleOverride : 
                     targetStatus === 'initial_design' ? "UPLOAD INITIAL DESIGN" :
                     targetStatus === 'sign_of_copy' ? "UPLOAD SIGN OFF COPY" :
                     targetStatus === 'prod_drawings' ? "UPLOAD PROD DRAWINGS" :
                     targetStatus ? `UPLOAD FILES (${targetStatus.replace(/_/g, ' ').toUpperCase()})` :
                     "UPLOAD DESIGN MEDIA";
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingMedia, setExistingMedia] = useState([]);
  const [fileToDelete, setFileToDelete] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [sendReview, setSendReview] = useState(false);
  const [selectedSeniorDesigner, setSelectedSeniorDesigner] = useState("");
  const { user } = useAuthStore();
  const isDesigner = (user?.role || "").toLowerCase().trim() === "designer";

  const { team } = useTeam();
  const seniorDesigners = React.useMemo(() => {
    return (team || []).filter(u => {
      const r = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
      return r === "senior-designer" || r === "senior_designer" || r === "senior designer";
    });
  }, [team]);

  const { data: designsResponse } = useQuery({
    queryKey: ['designs-dropdown'],
    queryFn: async () => {
      const response = await designService.getDesigns({ limit: 1000 });
      return response?.data || response;
    },
    enabled: isOpen,
  });

  const designsData = designsResponse || {};
  const rawLeads = Array.isArray(designsData) ? designsData : (designsData?.data?.leads || designsData?.leads || designsData?.designs || designsData?.projects || []);

  // Sync existing media with rawLeads updates (from useQuery)
  useEffect(() => {
    if (isOpen && initialData && rawLeads.length > 0) {
      const leadFromDesigns = rawLeads.find(l => l._id === initialData._id);
      if (leadFromDesigns?.designMedia) {
        setExistingMedia(leadFromDesigns.designMedia);
      }
    }
  }, [rawLeads, isOpen, initialData]);

  // Handle modal open/close resets
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setSelectedLeadId(initialData._id);
        setTitle(`Design for ${initialData.customerName || initialData.firstName || 'Lead'}`);
        setExistingMedia(initialData.designMedia || []);
      } else {
        setSelectedLeadId("");
        setTitle("");
        setExistingMedia([]);
      }
      setDescription("");
      setFiles([]);
      setPreviews([]);
    }
  }, [isOpen, initialData]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
      const newPreviews = selectedFiles.map(f => f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
      setPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingMedia = (file) => {
    setFileToDelete(file);
  };

  const confirmRemoveMedia = async () => {
    if (!fileToDelete) return;
    try {
      const response = await fetch(`${BASE_URL}/api/design/${initialData._id}/${fileToDelete.fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      if (!response.ok) throw new Error("Failed to remove media");
      
      showSuccessMessage("Media removed successfully");
      setExistingMedia(prev => prev.filter(m => String(m.fileId) !== String(fileToDelete.fileId)));
      
      // Invalidate the 'verve-leads' query cache to automatically refresh background table without reload
      queryClient.invalidateQueries({ queryKey: ['verve-leads'] });
      // Also invalidate the dropdown
      queryClient.invalidateQueries({ queryKey: ['designs-dropdown'] });
    } catch (err) {
      showApiError(err);
    } finally {
      setFileToDelete(null);
    }
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeadId) return;
    if (!targetStatus && files.length === 0) return; // Files required if not moving stage

    setIsSubmitting(true);
    
    if (files.length === 0) {
      await onSubmit([]);
      setIsSubmitting(false);
      return;
    }
    
    const formDataArray = files.map(file => {
      const formData = new FormData();
      formData.append("media", file);
      formData.append("leadId", selectedLeadId);
      if (targetStatus) formData.append("type", targetStatus);
      if (title) formData.append("title", title);
      if (description) formData.append("description", description);
      return formData;
    });

    await onSubmit(formDataArray, sendReview, selectedSeniorDesigner);
    setIsSubmitting(false);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={modalTitle} 
      size="md"
      footer={
        <div className="flex gap-3 w-full">
          <Button variant="outline" className="flex-1" onClick={onClose} type="button">Cancel</Button>
          <Button
            type="submit"
            form="upload-design-form"
            variant="orange"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting || !selectedLeadId || (!targetStatus && files.length === 0) || (sendReview && !selectedSeniorDesigner)}
          >
            {submitLabel 
              ? submitLabel 
              : targetStatus 
                ? (files.length > 0 ? `Upload & Move to ${targetStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}` : `Move to ${targetStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`)
                : "Upload Design"
            }
          </Button>
        </div>
      }
    >
      <form id="upload-design-form" onSubmit={onFormSubmit} className="flex flex-col gap-3 pb-1">
        
        {!initialData && (
          <div className="flex flex-col gap-1.5 relative z-50">
            <SearchableSelect
              label={<>Select a Lead (Status is Design)<span className="text-red-500">*</span></>}
              value={selectedLeadId}
              onChange={(val) => setSelectedLeadId(val)}
              options={rawLeads.map(l => ({
                value: l._id,
                label: `${l.customId} - ${l.customerName || l.firstName || "Unknown"}`
              }))}
              placeholder="-- Select a Lead --"
            />
          </div>
        )}

        <FormInput
          label="Design Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Master Bedroom Renders"
          size="sm"
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">Description (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-brand-blue/20 resize-none h-10"
            placeholder="Add details about this media..."
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">Media Files <span className="text-red-500">*</span></label>
          
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer relative min-h-[80px]">
            <input 
              type="file" 
              multiple
              onChange={handleFileChange} 
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              required={!targetStatus && files.length === 0}
              accept={acceptFiles}
            />
            <UploadCloud className="w-6 h-6 text-slate-400 mb-1" />
            <span className="text-sm font-medium text-slate-700">
              {files.length > 0 ? "Click or drag to add more files" : "Click or drag file to upload"}
            </span>
            <span className="text-xs text-slate-500 mt-1">
              {acceptFiles ? `Supports ${acceptFiles} files` : "Supports any file type"}
            </span>
          </div>
        </div>

        {isDesigner && (
          <div className="flex flex-col gap-2 mt-2 mb-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendReview"
                checked={sendReview}
                onChange={(e) => {
                  setSendReview(e.target.checked);
                  if (!e.target.checked) setSelectedSeniorDesigner("");
                }}
                className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue cursor-pointer"
              />
              <label htmlFor="sendReview" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
                Send Review to Senior Designer
              </label>
            </div>
            
            {sendReview && (
              <div className="flex flex-col gap-1.5 mt-2 pl-6">
                <label className="text-xs font-medium text-slate-700">Select Senior Designer <span className="text-red-500">*</span></label>
                <select
                  value={selectedSeniorDesigner}
                  onChange={(e) => setSelectedSeniorDesigner(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-brand-blue/20"
                  required
                >
                  <option value="" disabled>-- Select Senior Designer --</option>
                  {seniorDesigners.map(sd => (
                    <option key={sd._id} value={sd._id}>{sd.name || `${sd.firstName || ''} ${sd.lastName || ''}`.trim() || 'Unknown'}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5 mt-1 w-full h-[100px] min-h-[100px] max-h-[100px]">
          <label className="text-[11px] font-bold text-slate-400 tracking-wider mb-2 uppercase">Uploaded Files</label>
          
          {(existingMedia.length > 0 || files.length > 0) ? (
            <div className="flex flex-col gap-3 h-full overflow-y-auto pr-1 custom-scrollbar">

              {files.map((file, idx) => (
                <div key={`new-${idx}`} className="group flex items-center bg-slate-50 p-3 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all gap-4 w-full relative">
                  <button 
                    type="button" 
                    onClick={() => handleRemoveFile(idx)}
                    className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-500 hover:text-red-500 hover:bg-red-50 transition-colors shadow-sm z-10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  
                  <div className="w-10 h-10 rounded-lg bg-white border border-slate-100 flex items-center justify-center overflow-hidden shrink-0 p-1.5">
                    {previews[idx] ? (
                      <img src={previews[idx]} alt="Preview" className="w-full h-full object-cover rounded" />
                    ) : (
                      file.type.startsWith('video/') ? <span className="text-lg">🎬</span> : <span className="text-lg">📄</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-slate-700 truncate mb-0.5" title={file.name}>
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-medium">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <span className="text-slate-300 text-[10px]">|</span>
                      <span className="text-[10px] text-blue-500 font-medium">
                        Ready to upload
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {existingMedia.map((file, idx) => {
                const isImage = file.mimetype?.startsWith('image/');
                const isVideo = file.mimetype?.startsWith('video/');
                const isPdf = file.mimetype === 'application/pdf';
                const isExcel = file.mimetype?.includes('spreadsheet') || file.mimetype?.includes('excel') || file.filename?.endsWith('.xls') || file.filename?.endsWith('.xlsx');
                const fileUrl = `${BASE_URL}/api/design/media/${file.fileId}`;

                return (
                  <div key={`existing-${idx}`} className="group flex items-center bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all gap-4 w-full">
                    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden shrink-0 border border-slate-100 p-1.5">
                      {isImage ? (
                        <img src={fileUrl} alt={file.filename} className="w-full h-full object-contain" />
                      ) : isVideo ? (
                        <span className="text-lg">🎬</span>
                      ) : isPdf ? (
                        <span className="text-lg">📄</span>
                      ) : isExcel ? (
                        <span className="text-lg">📊</span>
                      ) : (
                        <span className="text-lg">📁</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold text-slate-700 truncate mb-0.5" title={file.filename}>
                        {file.filename}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 font-medium">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                        <span className="text-slate-300 text-[10px]">•</span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          {file.filename?.split('.').pop()?.toUpperCase() || 'FILE'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isExcel && (
                        <TableActionButton
                          icon={Edit}
                          title="Update in Documents"
                          onClick={() => { onClose(); navigate('/documents'); }}
                        />
                      )}
                      <TableActionButton
                        icon={Trash2}
                        title="Delete"
                        variant="primary"
                        onClick={() => handleRemoveExistingMedia(file)}
                        className="hover:!text-slate-800"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-[#f8fafc] rounded-xl border border-slate-200 border-dashed">
              <span className="text-2xl mb-2 text-slate-300">📁</span>
              <span className="text-[11px] font-medium text-slate-500 text-center px-4">No uploaded files.</span>
            </div>
          )}
        </div>
        {children}
      </form>

      <ConfirmationModal
        isOpen={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={confirmRemoveMedia}
        title="Delete Media"
        message={`Are you sure you want to permanently delete "${fileToDelete?.filename}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        type="danger"
      />
    </Modal>
  );
};
